/**
 * WalletRoute — Lommebok, `/poeng` og `/:memberId/poeng` (en voksen kan se
 * andres). Redesignet i #156, steg 6e (Stell_Lommebok).
 *
 * Fra toppen:
 *   - ScreenHeader med tilbakeknapp til Premier (`/belonninger`), tittelen
 *     «Lommebok» og «Butikk» som knapp. En voksen som ser et annet medlems
 *     lommebok, går tilbake til Familie og får navnet (/api/members/basic) i
 *     tittelen, men ingen
 *     Butikk-knapp: den voksne skal ikke handle for barnet. Skallet tegner ikke
 *     sin egen topp her (`drawsOwnHeader`);
 *   - saldokortet (`bg-wallet`): saldoen med count-up, og ukas poeng fra
 *     medlemmets rad på ukestavla, som på Premier (6a). Kortet var `bg-ink`
 *     før, og ble lyst i mørkt tema; lommebokgradienten har verdier for begge;
 *   - 2×2-statistikken (`WalletStats`), som erstatter StreakHero;
 *   - historikken gruppert per dag (`groupByDay`).
 *   På nettbrett står historikken i en egen kolonne til høyre.
 *
 * Ingenting lagres ved siden av: saldoen er SUM over hovedboka (Anti-Pattern
 * 6), ukas poeng er tavlas egen SUM, og statistikken er streak-raden og
 * rekorduka slik API-et regner dem.
 *
 * POINT-02 enforcement (T-02-17 mitigation):
 *   - URL param `:memberId` may differ from the active session's id when
 *     an admin views a child's wallet. Non-admin viewers attempting to
 *     view another member's `/{:memberId}/poeng` get the 403 fallback
 *     (`wallet-403` testid). The API layer also enforces (Plan 02-04
 *     isOwnOrAdmin); this client gate is UX, not the security boundary.
 *
 * Count-up on approval (issue #79):
 *   L3ApprovedBurst dispatches `stell:countup` on `document` with
 *   {from, to} when one of the viewer's chores is approved; this route is the
 *   listener and hands `countUpFrom` to <PointBalance>. Two things make it
 *   correct rather than merely working:
 *     - The listener is gated on `isOwnWallet`, so an admin sitting on
 *       /{kidId}/poeng does not see the child's wallet animate off their own
 *       approval. The event carries no memberId and needs none — useCelebration
 *       only dispatches L3 for `claimed_by === viewerId`.
 *     - The event is stored as an INTENT and applied only while
 *       `balanceQuery.data === pending.to`. The celebration frame and the
 *       points/changed frame arrive on two separate sockets, so either order
 *       happens in the wild; matching on the target value makes both work.
 *   The intent expires after TIMINGS.l3.countupIntentTtl (the old toast
 *   lifetime, kept when #155 made L3 a full screen) so a stale {from,to} cannot replay the
 *   animation when an unrelated change later happens to land on `to`.
 *   Reduced motion needs nothing here — <PointBalance> reads the media query
 *   itself and snaps.
 *
 * Session loading: same local pattern as PoolBoardRoute / ApprovalsRoute
 * — direct GET /api/auth/me on mount, in-component state. No shared
 * useSession helper exists yet (a later refactor plan can DRY this).
 */
import { useEffect, useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { ScreenHeader } from '../../components/ScreenHeader';
import { EmptyState } from '../../components/primitives';
import { GLASS_SURFACE } from '../../components/primitives/Card';
import { PointBalance } from '../../components/primitives/PointBalance';
import { capitalizeFirst, formatDayHeader, formatPoints } from '../../i18n/formatters';
import { TIMINGS } from '../celebrations/timings';
import { useFamilyMembers } from '../chores/useFamilyMembers';
import { useWeeklyLeaderboard } from '../leaderboard/useLeaderboardQueries';
import { PREMIER_MAIN, WALLET_SURFACE } from '../rewards/PremierFrame';
import { TransactionRow } from './TransactionRow';
import { useBalance, useHistory } from './usePointsQueries';
import { groupByDay, type HistoryDay } from './walletHistory';
import { WalletStats } from './WalletStats';

interface SessionMember {
  id: string;
  name: string;
  short_name: string;
  role: 'admin' | 'teen' | 'child';
}

function useSession(): { viewer: SessionMember | null; loaded: boolean } {
  const [viewer, setViewer] = useState<SessionMember | null>(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await globalThis.fetch('/api/auth/me', {
          credentials: 'same-origin',
        });
        if (!r.ok) {
          if (!cancelled) setLoaded(true);
          return;
        }
        const body = (await r.json()) as { member?: SessionMember };
        if (!cancelled) {
          setViewer(body.member ?? null);
          setLoaded(true);
        }
      } catch {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return { viewer, loaded };
}

export function WalletRoute(): ReactNode {
  const { t } = useTranslation();
  const params = useParams<{ memberId?: string }>();
  const { viewer, loaded } = useSession();

  // Resolve target member: explicit URL param wins; otherwise active session.
  const targetMemberId = params.memberId ?? viewer?.id ?? '';
  const isAdmin = viewer?.role === 'admin';
  const isOwnWallet = viewer?.id === targetMemberId;
  const allowed = isOwnWallet || isAdmin;

  // Defer queries until the session has loaded AND the gate passed.
  const queriesEnabled = loaded && allowed && targetMemberId.length > 0;
  const balanceQuery = useBalance(targetMemberId, queriesEnabled);
  const historyQuery = useHistory(targetMemberId, 30, queriesEnabled);
  const history = historyQuery.data ?? [];

  // ── stell:countup (issue #79) — see the header note ──────────────────────
  const [pendingCountUp, setPendingCountUp] = useState<{
    from: number;
    to: number;
  } | null>(null);

  useEffect(() => {
    if (!isOwnWallet) return undefined;
    const handler = (e: Event): void => {
      // The detail comes off a CustomEvent, so treat it as untyped input.
      const detail = (e as CustomEvent).detail as
        | { from?: unknown; to?: unknown }
        | undefined;
      const from = detail?.from;
      const to = detail?.to;
      if (typeof from !== 'number' || !Number.isFinite(from)) return;
      if (typeof to !== 'number' || !Number.isFinite(to)) return;
      setPendingCountUp({ from, to });
    };
    document.addEventListener('stell:countup', handler);
    return () => {
      document.removeEventListener('stell:countup', handler);
    };
  }, [isOwnWallet]);

  useEffect(() => {
    if (!pendingCountUp) return undefined;
    const timer = setTimeout(() => {
      setPendingCountUp(null);
    }, TIMINGS.l3.countupIntentTtl);
    return () => {
      clearTimeout(timer);
    };
  }, [pendingCountUp]);

  // Order-independent: only animate once the balance query has caught up to
  // the value the celebration said we were counting up to.
  const countUpFrom =
    pendingCountUp && balanceQuery.data === pendingCountUp.to
      ? pendingCountUp.from
      : undefined;

  const { data: weekly } = useWeeklyLeaderboard(queriesEnabled);
  const weekRow = weekly?.find((row) => row.memberId === targetMemberId);
  // En voksen som ser et annet medlems lommebok: navnet i tittelen, og
  // tilbake til Familie. Alle andre (også 403-siden) går tilbake til Premier.
  const viewingOther = loaded && isAdmin && !isOwnWallet;
  const { data: members } = useFamilyMembers(viewingOther);
  const otherName = viewingOther ? members?.find((m) => m.id === targetMemberId)?.short_name : undefined;

  const header = (
    <ScreenHeader
      back={
        viewingOther
          ? { to: '/foreldre', label: t('points.back.family'), testId: 'wallet-back' }
          : { to: '/belonninger', label: t('points.back.premier'), testId: 'wallet-back' }
      }
      title={otherName ? t('points.titleOther', { name: otherName }) : t('rewards.wallet.label')}
      trailing={
        isOwnWallet ? (
          <Link
            to="/butikk"
            data-testid="wallet-shop-link"
            className="inline-flex h-11 shrink-0 items-center justify-center rounded-pill border border-secondary-line bg-secondary px-4 text-sm font-extrabold text-ink hover:bg-inset"
          >
            {t('nav.rewardsTabs.shop')}
          </Link>
        ) : undefined
      }
    />
  );

  // Venter på /me: toppen står, så navigasjonen aldri forsvinner.
  if (!loaded) {
    return (
      <main data-testid="wallet-loading" className={PREMIER_MAIN}>
        {header}
        <p className="m-0 text-sm text-ink-2">{t('approvals.loading')}</p>
      </main>
    );
  }

  // POINT-02 client gate (server is the authoritative gate).
  if (!allowed) {
    return (
      <main data-testid="wallet-403" className={PREMIER_MAIN}>
        {header}
        <p className="m-0 py-8 text-center text-sm text-ink-2">{t('points.forbidden')}</p>
      </main>
    );
  }

  return (
    <main data-testid="wallet-route" className={PREMIER_MAIN}>
      {header}
      <div className="grid gap-3 lg:grid-cols-2 lg:items-start lg:gap-6">
        <div className="flex min-w-0 flex-col gap-3">
          <section
            data-testid="wallet-hero"
            aria-label={t('points.balanceLabel')}
            className={`${WALLET_SURFACE} p-4.5`}
          >
            <p className="m-0 text-control font-bold text-ink-2">{t('points.balanceLabel')}</p>
            {/* Ukjent saldo (henter eller feilet) er ikke 0, som på Premier. */}
            <p className="m-0">
              {balanceQuery.data === undefined ? (
                <span className="text-display-m leading-tight font-extrabold">{t('rewards.wallet.pending')}</span>
              ) : (
                <PointBalance balance={balanceQuery.data} countUpFrom={countUpFrom} />
              )}
              <span className="ml-1 text-heading-m font-extrabold">{t('points.unit')}</span>
            </p>
            {weekRow ? (
              <p data-testid="wallet-week" className="tnum m-0 text-body-s font-extrabold text-good">
                {t('rewards.wallet.week', { points: formatPoints(weekRow.points) })}
              </p>
            ) : null}
          </section>
          {/* Nøkkelen nullstiller «Ny start!» når en voksen bytter medlem. */}
          <WalletStats key={targetMemberId} memberId={targetMemberId} />
        </div>

        <section data-testid="wallet-history" aria-labelledby="wallet-history-heading" className="flex min-w-0 flex-col gap-2">
          <h2
            id="wallet-history-heading"
            className="m-0 mt-1 text-caption font-bold tracking-wider text-ink-2 uppercase"
          >
            {t('points.history.heading')}
          </h2>
          {/* Plan 06-02 Task 2: <EmptyState variant="inline"> med
              points.empty.{heading,body}. Ytre element bærer
              wallet-empty-kontrakten. */}
          {history.length === 0 ? (
            <div data-testid="wallet-empty">
              <EmptyState
                variant="inline"
                heading={t('points.empty.heading')}
                body={t('points.empty.body')}
              />
            </div>
          ) : (
            <div data-testid="wallet-history-list" className={`${GLASS_SURFACE} rounded-lg px-3.5 py-1 shadow-md`}>
              {groupByDay(history).map((group) => (
                <HistoryGroup key={group.day.toISOString()} group={group} />
              ))}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

function HistoryGroup({ group }: { group: HistoryDay }): ReactNode {
  const { t } = useTranslation();
  const headingId = useId();
  const heading =
    group.kind === 'other' ? capitalizeFirst(formatDayHeader(group.day)) : t(`points.history.${group.kind}`);
  return (
    <section aria-labelledby={headingId}>
      <h3 id={headingId} data-testid="wallet-history-day" className="m-0 pt-2.5 pb-0.5 text-caption font-extrabold text-ink-2">
        {heading}
      </h3>
      <ul className="m-0 list-none divide-y divide-card-line p-0">
        {group.rows.map((tx) => (
          <TransactionRow key={tx.id} tx={tx} />
        ))}
      </ul>
    </section>
  );
}
